export class AudioStreamManager {
  private inputAudioContext: AudioContext | null = null;
  private outputAudioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;

  private workletNode: AudioWorkletNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private analyser: AnalyserNode | null = null;
  private rafId: number | null = null;

  // Audio Queue
  private nextStartTime: number = 0;
  private scheduledSources: Set<AudioBufferSourceNode> = new Set();

  // Callbacks
  public onAudioData: (base64: string) => void = () => {};
  public onAudioLevel: (level: number) => void = () => {};
  public onQueueEmpty: () => void = () => {};

  private static INPUT_SAMPLE_RATE = 16000;
  private static OUTPUT_SAMPLE_RATE = 24000;

  constructor() {}

  public async initialize() {
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    this.inputAudioContext = new AudioContextClass({
      sampleRate: AudioStreamManager.INPUT_SAMPLE_RATE,
    });
    this.outputAudioContext = new AudioContextClass({
      sampleRate: AudioStreamManager.OUTPUT_SAMPLE_RATE,
    });

    await Promise.all([
      this.inputAudioContext.resume(),
      this.outputAudioContext.resume(),
    ]);

    // Setup Analyser for Output
    this.analyser = this.outputAudioContext.createAnalyser();
    this.analyser.fftSize = 256;
    this.analyser.connect(this.outputAudioContext.destination);

    this.startAudioLevelMonitoring();
  }

  public async startInput(stream: MediaStream) {
    if (!this.inputAudioContext) return;
    this.mediaStream = stream;

    // Worklet Setup
    const workletUrl = this.createWorkletUrl();
    await this.inputAudioContext.audioWorklet.addModule(workletUrl);
    URL.revokeObjectURL(workletUrl);

    this.sourceNode = this.inputAudioContext.createMediaStreamSource(stream);
    this.workletNode = new AudioWorkletNode(
      this.inputAudioContext,
      "pcm-processor",
    );

    this.workletNode.port.onmessage = (event) => {
      const pcmBuffer = event.data;
      if (pcmBuffer && pcmBuffer.byteLength > 0) {
        const base64 = this.convertToBase64(pcmBuffer);
        this.onAudioData(base64);
      }
    };

    this.sourceNode.connect(this.workletNode);
    // Connect to destination (muted) to keep worklet alive
    const gain = this.inputAudioContext.createGain();
    gain.gain.value = 0;
    this.workletNode.connect(gain);
    gain.connect(this.inputAudioContext.destination);
  }

  public async playAudioChunk(base64Audio: string) {
    if (!this.outputAudioContext) return;

    try {
      const audioBuffer = await this.decodeAudio(base64Audio);
      this.playAudioBuffer(audioBuffer);
    } catch (e) {
      console.error("Audio playback error", e);
    }
  }

  public playAudioBuffer(audioBuffer: AudioBuffer) {
    if (!this.outputAudioContext) return;

    const source = this.outputAudioContext.createBufferSource();
    source.buffer = audioBuffer;

    // Connect to analyser instead of destination directly
    if (this.analyser) {
      source.connect(this.analyser);
    } else {
      source.connect(this.outputAudioContext.destination);
    }

    const now = this.outputAudioContext.currentTime;
    const start = Math.max(now, this.nextStartTime);
    source.start(start);
    this.nextStartTime = start + audioBuffer.duration;

    this.scheduledSources.add(source);
    source.onended = () => {
      this.scheduledSources.delete(source);
      if (this.scheduledSources.size === 0) {
        this.onQueueEmpty();
      }
    };
  }

  public stopAll() {
    this.mediaStream?.getTracks().forEach((t) => t.stop());
    this.scheduledSources.forEach((s) => {
      try {
        s.stop();
      } catch {
        // ignore
      }
    });
    this.scheduledSources.clear();

    // Stop monitoring
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    this.workletNode?.disconnect();
    this.sourceNode?.disconnect();
    this.analyser?.disconnect();

    this.inputAudioContext?.close();
    this.outputAudioContext?.close();

    this.inputAudioContext = null;
    this.outputAudioContext = null;
    this.analyser = null;
    this.mediaStream = null;
    this.nextStartTime = 0;
  }

  public async suspend() {
    await this.inputAudioContext?.suspend();
    await this.outputAudioContext?.suspend();
  }

  public async suspendInput() {
    await this.inputAudioContext?.suspend();
    // Send 0 level when suspended
    this.onAudioLevel(0);
  }

  public async resumeInput() {
    if (this.inputAudioContext?.state !== "running") {
      await this.inputAudioContext?.resume();
    }
  }

  public async resume() {
    await this.inputAudioContext?.resume();
    await this.outputAudioContext?.resume();
  }

  public clearAudioQueue() {
    this.scheduledSources.forEach((s) => {
      try {
        s.stop();
      } catch {
        // ignore
      }
    });
    this.scheduledSources.clear();

    if (this.outputAudioContext) {
      this.nextStartTime = this.outputAudioContext.currentTime;
    } else {
      this.nextStartTime = 0;
    }
  }

  public get hasOutputContext() {
    return !!this.outputAudioContext;
  }

  public get isQueueEmpty() {
    return this.scheduledSources.size === 0;
  }

  public get currentTime() {
    return this.outputAudioContext?.currentTime || 0;
  }

  private startAudioLevelMonitoring() {
    const updateLevel = () => {
      if (!this.analyser || !this.outputAudioContext) return;

      const dataArray = new Uint8Array(this.analyser.frequencyBinCount);
      this.analyser.getByteFrequencyData(dataArray);

      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];

      const average = sum / dataArray.length;
      // Normalize to 0-1 range roughly
      this.onAudioLevel(average / 255);

      this.rafId = requestAnimationFrame(updateLevel);
    };
    this.rafId = requestAnimationFrame(updateLevel);
  }

  private createWorkletUrl() {
    const workletCode = `
      class PCMProcessor extends AudioWorkletProcessor {
        process(inputs, outputs, parameters) {
          const input = inputs[0];
          if (input.length > 0) {
            const float32Data = input[0];
            const int16Data = new Int16Array(float32Data.length);
            for (let i = 0; i < float32Data.length; i++) {
              const s = Math.max(-1, Math.min(1, float32Data[i]));
              int16Data[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
            }
            this.port.postMessage(int16Data.buffer, [int16Data.buffer]);
          }
          return true;
        }
      }
      registerProcessor('pcm-processor', PCMProcessor);
    `;
    return URL.createObjectURL(
      new Blob([workletCode], { type: "application/javascript" }),
    );
  }

  private convertToBase64(buffer: ArrayBuffer): string {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i++)
      binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  }

  private async decodeAudio(base64: string): Promise<AudioBuffer> {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);

    // Gemini Live returns raw PCM 16-bit 24kHz
    const pcmData = new Int16Array(bytes.buffer);
    const audioBuffer = this.outputAudioContext!.createBuffer(
      1,
      pcmData.length,
      AudioStreamManager.OUTPUT_SAMPLE_RATE,
    );
    const channelData = audioBuffer.getChannelData(0);

    for (let i = 0; i < pcmData.length; i++) {
      channelData[i] = pcmData[i] / 32768; // Convert Int16 to Float32 [-1, 1]
    }

    return audioBuffer;
  }

  // For decoding external use if needed
  public async decodeAudioBuffer(base64: string): Promise<AudioBuffer> {
    return this.decodeAudio(base64);
  }
}
